/**
 * Google Analytics 4 wrapper — GDPR-strict Consent Mode v2.
 *
 * Design rationale:
 * - **Env-configurable**. Skipped entirely when REACT_APP_GA_MEASUREMENT_ID
 *   is empty (preview/local dev), so no PII leaks to the wrong property.
 * - **Consent Mode v2 default = ALL DENIED**. gtag.js is loaded but does
 *   NOT fire tracking pings until `grantConsent()` writes an updated
 *   consent state. This is the GDPR-safe posture — mirrors Google's
 *   own recommendation for EU-facing sites.
 * - **SPA-aware**. GA4 auto-collects `page_view` only for hard navs;
 *   React Router client-side transitions need an explicit trackPageView.
 * - **Idempotent init**. If initAnalytics is called twice (StrictMode,
 *   HMR), the second call is a no-op.
 */

const MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || "";
const CONSENT_KEY = "facetra_ga_consent"; // "granted" | "denied" | null

let initialised = false;

/** Lazy access to gtag with a safe no-op when analytics is disabled. */
function gtag(...args) {
  if (typeof window === "undefined") return;
  if (!window.dataLayer) window.dataLayer = [];
  window.dataLayer.push(args);
}

export function isAnalyticsEnabled() {
  return Boolean(MEASUREMENT_ID);
}

export function getStoredConsent() {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem(CONSENT_KEY);
  } catch {
    return null;
  }
}

function storeConsent(state) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(CONSENT_KEY, state);
  } catch {
    /* localStorage disabled — fine, session-only consent */
  }
}

/**
 * Boot GA4 exactly once. Safe to call before user consent — Consent
 * Mode v2 keeps everything denied until we push an update.
 */
export function initAnalytics() {
  if (initialised || !isAnalyticsEnabled() || typeof window === "undefined") return;
  initialised = true;

  window.dataLayer = window.dataLayer || [];
  window.gtag = function () {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments);
  };

  // Consent Mode v2 — start denied. Any prior stored choice is
  // replayed immediately below so returning users skip the banner.
  window.gtag("consent", "default", {
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    analytics_storage: "denied",
    functionality_storage: "granted", // needed for the SPA to work
    security_storage: "granted",
    wait_for_update: 500,
  });

  window.gtag("js", new Date());
  window.gtag("config", MEASUREMENT_ID, {
    // Don't auto-fire page_view — the SPA route tracker owns it, so
    // we get exactly one event per client-side transition without
    // duplicate hits on the initial mount.
    send_page_view: false,
    anonymize_ip: true,
  });

  // Inject the gtag.js script.
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
  document.head.appendChild(s);

  // Replay a previously-stored consent so we don't nag returning
  // visitors. New visitors stay in the default-denied state until
  // they act on the banner.
  const prior = getStoredConsent();
  if (prior === "granted") grantConsent({ silent: true });
  else if (prior === "denied") denyConsent({ silent: true });
}

export function grantConsent({ silent = false } = {}) {
  if (!isAnalyticsEnabled() || typeof window === "undefined") return;
  window.gtag && window.gtag("consent", "update", {
    ad_storage: "granted",
    ad_user_data: "granted",
    ad_personalization: "granted",
    analytics_storage: "granted",
  });
  if (!silent) storeConsent("granted");
  // Fire the initial page_view now that consent is in — the very
  // first landing hit otherwise never reaches GA.
  trackPageView(window.location.pathname + window.location.search);
}

export function denyConsent({ silent = false } = {}) {
  if (!isAnalyticsEnabled() || typeof window === "undefined") return;
  window.gtag && window.gtag("consent", "update", {
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    analytics_storage: "denied",
  });
  if (!silent) storeConsent("denied");
}

/**
 * Clear the stored consent choice and re-show the banner. Used by the
 * "Cookie preferences" link in the footer so a user can revoke or
 * change their mind at any time (GDPR right of withdrawal). Fires a
 * DOM event the banner listens to instead of reload-hacks — keeps the
 * SPA state intact.
 */
export const CONSENT_RESET_EVENT = "facetra:consent-reset";

export function resetConsent() {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(CONSENT_KEY);
  } catch {
    /* localStorage disabled — nothing to clear */
  }
  // Downgrade current consent so no more hits go out until the user
  // re-picks. Safe even if analytics is disabled — the guard inside
  // denyConsent handles that.
  denyConsent({ silent: true });
  window.dispatchEvent(new CustomEvent(CONSENT_RESET_EVENT));
}

/** Fire a page_view for a SPA route change. Consent gate handled by GA4. */
export function trackPageView(path) {
  if (!isAnalyticsEnabled()) return;
  gtag("event", "page_view", {
    page_path: path,
    page_location: typeof window !== "undefined" ? window.location.href : path,
    page_title: typeof document !== "undefined" ? document.title : "",
  });
}

/** Thin wrapper for product events. Namespaced to avoid gtag collisions. */
export function trackEvent(name, params = {}) {
  if (!isAnalyticsEnabled() || !name) return;
  gtag("event", name, params);
}
